import { useCallback, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { OnlyOfficeEditor } from '@workpay/ds-shared/components/OnlyOfficeEditor';
import { ChatWidget } from '@workpay/ds-shared/components/ChatWidget';
import AlertModal from '@components/ui/AlertModal/AlertModal';
import { useTranslation } from '@hooks/useTranslation';
import { useNotificationStore } from '@stores/notificationStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import type { DocumentSnapshot } from '@workpay/ds-shared/types/snapshot';
import { useReviewerSession } from './useReviewerSession';
import { ReviewerHeader } from './ReviewerHeader';
import { ReviewerEndedScreen } from './ReviewerEndedScreen';
import styles from './ReviewerView.module.scss';

interface ReviewerViewProps {
  snapshotId: string;
  snapshot: DocumentSnapshot;
  backTo: string;
}

/**
 * Session-authed internal reviewer experience for a contract snapshot.
 *
 * Used by:
 * - `/t/contracts/{id}?review=true` (tenant user reviewing their own org's snapshot)
 *
 * Future:
 * - `/w/contracts/{id}?review=true` (workpay colleague added as INTERNAL reviewer)
 *
 * The component is surface-agnostic — it works with any `snapshot` payload
 * (tenant `dsSnapshotService` vs workpay `dsCrossOrgSnapshotService`) because
 * the reviewer-specific endpoints (my-review, reviewer-activities, reviewer-*
 * mutations) are universal.
 */
export function ReviewerView({ snapshotId, snapshot, backTo }: ReviewerViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);

  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);

  const { state, myReview, reviewForChat, submit, complete, anyPending } = useReviewerSession({
    snapshotId,
    snapshot,
    enabled: true,
  });

  const handleSubmit = useCallback(() => {
    submit.mutate(undefined, {
      onSuccess: () => {
        addNotification({
          variant: 'success',
          title: t('K_REVIEWER_SUBMIT_SUCCESS', 'Changes submitted to author.'),
        });
      },
      onError: (error) => {
        const e = error as WorkpayApiError;
        addNotification({
          variant: 'critical',
          title: t(e.description, e.description),
        });
      },
    });
  }, [submit, addNotification, t]);

  const handleCompleteConfirmed = useCallback(() => {
    complete.mutate(undefined, {
      onSuccess: () => {
        addNotification({
          variant: 'success',
          title: t('K_REVIEWER_COMPLETE_SUCCESS', 'Review marked complete.'),
        });
        setShowCompleteConfirm(false);
        // Route back to list. The snapshot has just transitioned out of IN_REVIEW
        // (dual-completion met) so staying on the detail is confusing.
        navigate({ to: backTo });
      },
      onError: (error) => {
        setShowCompleteConfirm(false);
        const e = error as WorkpayApiError;
        addNotification({
          variant: 'critical',
          title: t(e.description, e.description),
        });
      },
    });
  }, [complete, addNotification, t, navigate, backTo]);

  // ChatWidget invalidation bridge — our activity query key is different from
  // the ChatSidebar's hardcoded default. onNoteSent tells it to delegate.
  const handleNoteSent = useCallback(() => {
    if (!myReview?.reviewId) return;
    void queryClient.invalidateQueries({
      queryKey: ['dsSnapshot-reviewer-activities', myReview.reviewId],
    });
  }, [queryClient, myReview?.reviewId]);

  // ── Terminal states ─────────────────────────────────────────────────
  if (state.kind === 'ended') {
    return <ReviewerEndedScreen reason={state.reason} backTo={backTo} />;
  }

  if (state.kind === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.editorLoading}>
          <div className={styles.spinner} />
          {t('K_REVIEWER_LOADING', 'Loading review...')}
        </div>
      </div>
    );
  }

  const isEditorReady = state.kind === 'ready';

  // ── Main layout ─────────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      <ReviewerHeader
        title={snapshot.name}
        reviewerName={state.myReview.reviewerName}
        backTo={backTo}
        submitPending={submit.isPending}
        completePending={complete.isPending}
        anyPending={anyPending}
        onSubmit={handleSubmit}
        onComplete={() => setShowCompleteConfirm(true)}
      />

      <div className={styles.body}>
        <div className={styles.editorContainer}>
          {!isEditorReady ? (
            <div className={styles.editorLoading}>
              <div className={styles.spinner} />
              {t('K_REVIEWER_LOADING', 'Loading review...')}
            </div>
          ) : (
            <OnlyOfficeEditor
              fileId={snapshot.sourceFileId}
              mode="edit"
              configOverride={state.editorConfig}
            />
          )}
        </div>

        {reviewForChat && (
          <ChatWidget
            targetType="TEMPLATE"
            targetId={snapshotId}
            reviews={[reviewForChat]}
            mode="reviewer-internal"
            reviewId={state.myReview.reviewId}
            onNoteSent={handleNoteSent}
            positioning="absolute"
          />
        )}
      </div>

      <AlertModal
        variant="warning"
        title={t('K_REVIEWER_COMPLETE_CONFIRM_TITLE', 'Mark review complete?')}
        description={t(
          'K_REVIEWER_COMPLETE_CONFIRM_BODY',
          'This will close the review. You will not be able to make further changes.',
        )}
        isOpen={showCompleteConfirm}
        onClose={() => !complete.isPending && setShowCompleteConfirm(false)}
        primaryLabel={t('K_BUTTON_MARK_COMPLETE', 'Mark as Complete')}
        onPrimaryClick={handleCompleteConfirmed}
        loading={complete.isPending}
      />
    </div>
  );
}
